import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import LoadingSpinner from '@/shared/ui/LoadingSpinner';

/**
 * RequireAuth - Guard that requires user to be authenticated
 * Redirects to login with returnUrl if not authenticated
 */
export const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <LoadingSpinner className="border-purple-600" fullPage={false} />
          <p className="mt-4 text-gray-600">{t('common:loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Save the attempted URL for redirecting after login
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  return <>{children}</>;
};

/**
 * RequireRole - Guard that requires specific role(s)
 * @param {string[]} allowedRoles - Array of allowed roles (e.g., ['ADMIN'], ['SELLER', 'ADMIN'])
 * @param {string} redirectTo - Optional custom redirect path
 */
export const RequireRole = ({
  allowedRoles,
  redirectTo,
  children,
}: {
  allowedRoles: string[];
  redirectTo?: string;
  children: React.ReactNode;
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <LoadingSpinner className="border-purple-600" fullPage={false} />
          <p className="mt-4 text-gray-600">Vérification des accès...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  // Normalize role comparison
  const userRole = user.role?.toUpperCase();
  const normalizedAllowedRoles = allowedRoles.map((r) => r.toUpperCase());

  // Check if user has required role
  if (!normalizedAllowedRoles.includes(userRole)) {
    // Redirect to unauthorized page or their appropriate dashboard
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

/**
 * AdminRoute - Shorthand for admin-only routes
 */
export const AdminRoute = ({ children }: { children: React.ReactNode }) => (
  <RequireRole allowedRoles={['ADMIN']}>{children}</RequireRole>
);

/**
 * SellerRoute - For seller-only routes (/seller/*)
 * Only SELLER or ADMIN can access
 * NOTE: PARTICULIER and PROFESSIONNEL cannot access /seller/*
 */
export const SellerRoute = ({ children }: { children: React.ReactNode }) => (
  <RequireRole allowedRoles={['SELLER', 'ADMIN']}>{children}</RequireRole>
);

/**
 * ClientRoute - For client routes
 * PARTICULIER and PROFESSIONNEL can access (PRO can access client routes per matrix)
 * NOTE: SELLER cannot access /client/* per security matrix
 */
export const ClientRoute = ({ children }: { children: React.ReactNode }) => (
  <RequireRole allowedRoles={['PARTICULIER', 'PROFESSIONNEL', 'ADMIN']}>{children}</RequireRole>
);

/**
 * ProfessionalRoute - For PROFESSIONNEL-only routes (/pro/*)
 * Only PROFESSIONNEL or ADMIN can access
 * NOTE: PARTICULIER and SELLER cannot access /pro/*
 */
export const ProfessionalRoute = ({ children }: { children: React.ReactNode }) => (
  <RequireRole allowedRoles={['PROFESSIONNEL', 'ADMIN']}>{children}</RequireRole>
);

/**
 * RedirectByRole - Redirects user to their appropriate dashboard based on role
 */
export const RedirectByRole = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <LoadingSpinner className="border-purple-600" fullPage={false} />
      </div>
    );
  }

  if (!user) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  const userRole = user.role?.toUpperCase();

  switch (userRole) {
    case 'ADMIN':
      return <Navigate to="/admin/dashboard" replace />;
    case 'SELLER':
      return <Navigate to="/seller/dashboard" replace />;
    case 'PROFESSIONNEL':
      return <Navigate to="/pro/dashboard" replace />;
    case 'PARTICULIER':
    default:
      return <Navigate to="/client/dashboard" replace />;
  }
};

export default RequireAuth;
